import { ColDef, GridReadyEvent, ICellRendererParams } from 'ag-grid-community'
import { removeTeamMember } from 'api/teams'
import { PLACEHOLDER } from 'components/column-renderers'
import dateRenderer from 'components/column-renderers/date'
import ConfirmRemoveModal from 'components/modals/ConfirmRemoveModal'
import InfinityAgGrid from 'components/table/InfinityAgGrid'
import { columnCreatedOrUpdatedBy, getActionColumn, RowAction, UIModal } from 'features/UI'
import { useShowControl } from 'hooks/useShowControl'
import React, { SyntheticEvent, useCallback, useMemo, useState } from 'react'
import { TeamInfoMember, TraineeTeamRole } from 'types'
import { getRolesList } from '../../models/Team.model'

export function TeamsMembersCell({
  members,
  teamId,
  onRefetch,
  readonly,
}: {
  members: TeamInfoMember[]
  teamId: number
  onRefetch?: () => void
  readonly?: boolean
}) {
  const [isOpen, onOpen, onClose] = useShowControl()
  const [isRemoveModalOpen, onOpenRemoveModal, onCloseRemoveModal] = useShowControl()
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null)
  const [isRemoving, setIsRemoving] = useState(false)

  const handleOpen = (event: SyntheticEvent) => {
    event.preventDefault()
    onOpen()
  }

  const handleGridReady = (event: GridReadyEvent) => {
    event.api.sizeColumnsToFit()
  }

  const handleRemoveMember = useCallback(
    (memberId: number) => {
      setSelectedMemberId(memberId)
      onOpenRemoveModal()
    },
    [onOpenRemoveModal],
  )

  const handleConfirmRemove = useCallback(async () => {
    if (!selectedMemberId) return

    try {
      setIsRemoving(true)
      await removeTeamMember({
        id: teamId,
        memberId: selectedMemberId,
      })
      onCloseRemoveModal()
      setSelectedMemberId(null)
      onClose()
      onRefetch?.()
    } catch (error) {
      console.error('Failed to remove member:', error)
    } finally {
      setIsRemoving(false)
    }
  }, [selectedMemberId, teamId, onCloseRemoveModal, onClose, onRefetch])

  const handleCloseRemoveModal = useCallback(() => {
    if (!isRemoving) {
      onCloseRemoveModal()
      setSelectedMemberId(null)
    }
  }, [isRemoving, onCloseRemoveModal])

  const actions: RowAction[] = useMemo(() => {
    return [
      {
        name: 'Remove Member',
        onClick: handleRemoveMember,
      },
    ]
  }, [handleRemoveMember])

  const columnDefs: ColDef[] = useMemo(() => {
    const cols = [
      {
        headerName: 'First Name',
        field: 'firstName',
      },
      {
        headerName: 'Last Name',
        field: 'lastName',
      },
      {
        headerName: 'Email',
        field: 'email',
      },
      columnCreatedOrUpdatedBy({ propName: 'creator', label: 'Invited by', colId: 'createdBy' }),
      {
        headerName: 'Role',
        field: 'role',
        minWidth: 400,
        cellRenderer: (
          props: ICellRendererParams<{
            role: TraineeTeamRole[]
            otherRole: string
          }>,
        ) => {
          if (!props.data) return PLACEHOLDER

          const { role, otherRole } = props.data
          if (!role || role.length === 0) return PLACEHOLDER

          const roleList = getRolesList(role, otherRole)
          return roleList.join(', ')
        },
      },
      {
        headerName: 'Date',
        cellRenderer: dateRenderer('createdAt'),
        colId: 'createdAt',
      },
    ]

    return readonly ? cols : cols.concat(getActionColumn(actions))
  }, [actions, readonly])

  const selectedMember = useMemo(() => {
    return members.find((m) => m.id === selectedMemberId)
  }, [members, selectedMemberId])

  return (
    <div>
      <a href="" onClick={handleOpen}>
        {members.length}
      </a>

      <UIModal isOpen={isOpen} onClose={onClose} width={1188} title="Members">
        <div
          className="ag-theme-alpine"
          style={{ height: '400px', width: '1140px', fontSize: '1rem' }}
        >
          <InfinityAgGrid
            rowData={members}
            columnDefs={columnDefs}
            rowModelType="clientSide"
            onGridReady={handleGridReady}
            floatingFiltersHeight={0}
          />
        </div>
      </UIModal>

      <ConfirmRemoveModal
        isOpen={isRemoveModalOpen}
        entityToRemove={
          selectedMember
            ? `${selectedMember.firstName} ${selectedMember.lastName} (${selectedMember.email})`
            : 'this member'
        }
        loading={isRemoving}
        handleClose={handleCloseRemoveModal}
        handleConfirm={handleConfirmRemove}
      />
    </div>
  )
}
